import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaTruck, FaUserPlus, FaEye, FaEyeSlash } from 'react-icons/fa'
import { authService } from '../services'
import { resolveReferralCode } from '../services/referral'
import { readReferral } from '../utils/referral'
import { useAuthStore } from '../store'
import { Button, Input } from '../components/common'

export default function SignupPage() {
  const navigate = useNavigate()
  const { init } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // 渠道归因。referral 是本地存的 { token, landingPath }；
  // resolvedCode 仅用于只读回显，绝不参与提交（提交的是 token，由后端自行解析）。
  const [referral, setReferral] = useState(null)
  const [resolvedCode, setResolvedCode] = useState('')
  const [showManualCode, setShowManualCode] = useState(false)

  useEffect(() => {
    const stored = readReferral()
    if (!stored) return
    setReferral(stored)
    // 解析失败/超时一律静默：不转圈、不报错。表单保持完全可用，
    // 提交仍携带令牌，归因不受影响。
    resolveReferralCode(stored.token).then((code) => {
      if (code) setResolvedCode(code)
    })
  }, [])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm()

  const password = watch('password')

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      await authService.signup({
        fullName: data.fullName,
        companyName: data.companyName,
        phone: data.phone,
        email: data.email,
        password: data.password,
        referralToken: referral?.token,
        referralCode: data.referralCode,
        landingUrl: referral?.landingPath,
      })
      // 注册成功后刷新认证状态并跳转到客户中心
      await init()
      toast.success('注册成功！欢迎使用物流集货平台')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      toast.error(error.message || '注册失败，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
      {/* Header */}
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-primary-600 text-xl font-bold mb-6">
          <FaTruck className="text-2xl" />
          <span>物流集货平台</span>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">创建账户</h1>
        <p className="text-gray-500">注册成为会员，享受专属物流服务</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Input
          label="姓名"
          type="text"
          placeholder="请输入您的姓名"
          required
          error={errors.fullName?.message}
          {...register('fullName', {
            required: '请输入姓名',
            minLength: { value: 2, message: '姓名至少2个字符' },
          })}
        />

        {/* 公司名称选填：个人客户没有公司，必填会把他们挡在注册之外。
            后端同样把它当选填（PortalSignupDTO 上只有 fullName 有 @NotBlank）。 */}
        <Input
          label="公司名称"
          type="text"
          placeholder="选填，个人用户可不填"
          error={errors.companyName?.message}
          {...register('companyName')}
        />

        <Input
          label="手机号码"
          type="tel"
          placeholder="请输入手机号码"
          required
          error={errors.phone?.message}
          {...register('phone', {
            required: '请输入手机号码',
            pattern: {
              value: /^1[3-9]\d{9}$/,
              message: '请输入有效的手机号码',
            },
          })}
        />

        <Input
          label="邮箱地址"
          type="email"
          placeholder="请输入邮箱地址"
          required
          error={errors.email?.message}
          {...register('email', {
            required: '请输入邮箱',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: '请输入有效的邮箱地址',
            },
          })}
        />

        <div>
          <label className="block mb-1.5 font-medium text-gray-700 text-sm">
            设置密码 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="请设置登录密码"
              className={`w-full px-4 py-2.5 border rounded-md text-base focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-12 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('password', {
                required: '请设置密码',
                minLength: { value: 6, message: '密码至少6个字符' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
          )}
        </div>

        <Input
          label="确认密码"
          type="password"
          placeholder="请再次输入密码"
          required
          error={errors.confirmPassword?.message}
          {...register('confirmPassword', {
            required: '请确认密码',
            validate: (value) => value === password || '两次输入的密码不一致',
          })}
        />

        {/*
          渠道归因入口。三条硬性约束：
          1) 不展示合作方名称、不展示令牌、不给识别反馈 —— 访客不该知道自己被归因到谁；
          2) 解析出码才渲染只读字段；无令牌或解析不出则【不渲染任何字段】，
             因为向占绝大多数、根本没有码的访客展示空推荐框，会引发
             「我是不是少拿了优惠」的疑虑，用真实摩擦换一个几乎没人用的输入；
          3) 回显不透明码是安全的 —— A7K2MN 确认「有推荐关系生效」却识别不出合作方。
        */}
        {resolvedCode ? (
          <Input
            label="推荐码"
            type="text"
            value={resolvedCode}
            readOnly
            className="bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        ) : showManualCode ? (
          <Input
            label="推荐码"
            type="text"
            placeholder="选填"
            {...register('referralCode')}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowManualCode(true)}
            className="text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            有推荐码？
          </button>
        )}

        <div className="flex items-start">
          <input
            type="checkbox"
            id="agreement"
            className="w-4 h-4 mt-1 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            {...register('agreement', { required: '请阅读并同意服务条款' })}
          />
          <label htmlFor="agreement" className="ml-2 text-sm text-gray-600">
            我已阅读并同意
            <Link to="/terms" className="text-primary-600 hover:underline">《服务条款》</Link>
            和
            <Link to="/privacy" className="text-primary-600 hover:underline">《隐私政策》</Link>
          </label>
        </div>
        {errors.agreement && (
          <p className="text-sm text-red-500">{errors.agreement.message}</p>
        )}

        <Button
          type="submit"
          size="lg"
          loading={isLoading}
          className="w-full"
          icon={<FaUserPlus />}
        >
          注册
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">或</span>
          </div>
        </div>

        <p className="text-center text-gray-600">
          已有账户？
          <Link to="/login" className="text-primary-600 font-medium hover:text-primary-700 ml-1">
            立即登录
          </Link>
        </p>
      </form>
    </div>
  )
}
